import React, { useState, useEffect } from "react";
import { KeyRound, Copy, RefreshCw, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND_URL } from "@/lib/api";
import { colors } from "@/lib/theme";
import { Spinner } from "@/components/peeps/ui";

export function PlanImport() {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [deduping, setDeduping] = useState(false);
  const [oldName, setOldName] = useState("");
  const [newName, setNewName] = useState("");
  const [renaming, setRenaming] = useState(false);

  const endpoint = `${BACKEND_URL}/api/chat-import`;

  useEffect(() => {
    api.getApiToken().then((r) => setToken(r.token)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const generate = async () => {
    if (token && !window.confirm("Generate a new token? Your current one will stop working immediately.")) return;
    setBusy(true);
    try {
      const r = await api.generateApiToken();
      setToken(r.token);
      toast("New API token generated");
    } catch (e) { toast(e.message || "Failed to generate token"); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke your API token? The import script will stop working until you generate a new one.")) return;
    setBusy(true);
    try { await api.revokeApiToken(); setToken(null); toast("Token revoked"); }
    catch (e) { toast(e.message || "Failed to revoke"); }
    finally { setBusy(false); }
  };

  const dedupe = async () => {
    if (!window.confirm("Scan chat-imported plans and merge duplicates (same plan on the same day)? Votes and RSVPs are kept.")) return;
    setDeduping(true);
    try {
      const r = await api.dedupeChatImport();
      toast(r.duplicates_removed > 0
        ? `Removed ${r.duplicates_removed} duplicate${r.duplicates_removed === 1 ? "" : "s"} across ${r.duplicate_sets_merged} plan${r.duplicate_sets_merged === 1 ? "" : "s"}`
        : "No duplicates found — you're clean");
    } catch (err) { toast(err.message || "Cleanup failed"); }
    finally { setDeduping(false); }
  };

  const renameSender = async () => {
    const o = oldName.trim(), n = newName.trim();
    if (!o || !n) { toast("Enter both the old and new name"); return; }
    setRenaming(true);
    try {
      const r = await api.renameChatSender(o, n);
      toast(r.updated > 0 ? `Updated ${r.updated} plan${r.updated === 1 ? "" : "s"}` : "No plans matched that name");
      if (r.updated > 0) { setOldName(""); setNewName(""); }
    } catch (e) { toast(e.message || "Rename failed"); }
    finally { setRenaming(false); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); toast("Copied"); }
    catch { toast("Copy failed"); }
  };

  const card = { background: colors.surfaceSecondary, border: `1px solid ${colors.border}`, borderRadius: 16 };
  const mono = { background: colors.surfaceTertiary, color: colors.faint };

  return (
    <div data-testid="plan-import-section" className="mt-4">
      <p className="font-display font-medium text-[11.5px] uppercase tracking-wide mb-2 ml-1" style={{ color: colors.muted }}>
        Automated plan import
      </p>
      <div style={card} className="p-4" data-testid="api-token-section">
        <p className="font-display text-[12.5px] leading-relaxed mb-3" style={{ color: colors.onSurfaceTertiary }}>
          Your script pushes the daily chat-plans CSV to the endpoint below using this token as a Bearer header. Rows are stored to your account — then pick which feed fills each group from that group's settings. Keep the token secret; anyone with it can import to your account.
          <span className="block mt-2 font-mono2 text-[10px] px-2 py-1.5 rounded-lg break-all" style={{ background: colors.surfaceTertiary, color: colors.faint }}>
            source_group,title,date,start_time,end_time,location,link,details,source_sender,source_timestamp
          </span>
        </p>

        {loading ? (
          <div className="flex justify-center py-3"><Spinner /></div>
        ) : token ? (
          <div className="flex items-center gap-2 rounded-xl px-3 py-2.5 mb-3" style={mono}>
            <KeyRound size={14} color={colors.brandSecondary} />
            <span data-testid="api-token-value" className="flex-1 font-mono2 text-[11px] break-all">{token}</span>
            <button data-testid="copy-api-token" onClick={() => copy(token)} className="shrink-0 p-1.5 rounded-lg" style={{ background: colors.surfaceSecondary }}>
              {copied ? <Check size={13} color={colors.success} /> : <Copy size={13} color={colors.onSurfaceTertiary} />}
            </button>
          </div>
        ) : (
          <p className="font-display text-[12px] mb-3" style={{ color: colors.faint }}>No token yet. Generate one to enable the script.</p>
        )}

        <div className="flex gap-2">
          <button data-testid="generate-api-token" onClick={generate} disabled={busy}
            className="flex items-center justify-center gap-2 flex-1 rounded-xl py-2.5 transition-transform active:scale-[0.99]" style={{ background: colors.brand }}>
            {busy ? <Spinner style={{ borderTopColor: colors.onBrand }} /> : <RefreshCw size={14} color={colors.onBrand} />}
            <span className="font-display font-bold text-[13px]" style={{ color: colors.onBrand }}>{token ? "Regenerate" : "Generate token"}</span>
          </button>
          {token && (
            <button data-testid="revoke-api-token" onClick={revoke} disabled={busy}
              className="flex items-center justify-center gap-2 rounded-xl py-2.5 px-4" style={{ background: colors.surfaceTertiary, border: `1px solid ${colors.border}` }}>
              <Trash2 size={14} color={colors.danger || "#e5484d"} />
              <span className="font-display font-bold text-[13px]" style={{ color: colors.danger || "#e5484d" }}>Revoke</span>
            </button>
          )}
        </div>

        <p className="font-display text-[11px] mt-3 mb-1" style={{ color: colors.muted }}>Endpoint</p>
        <div className="flex items-center gap-2 rounded-lg px-2 py-1.5" style={mono}>
          <span className="flex-1 font-mono2 text-[10.5px] break-all">POST {endpoint}</span>
          <button data-testid="copy-endpoint" onClick={() => copy(endpoint)} className="shrink-0 p-1 rounded-md" style={{ background: colors.surfaceSecondary }}>
            <Copy size={12} color={colors.onSurfaceTertiary} />
          </button>
        </div>

        {token && (
          <>
            <p className="font-display text-[11px] mt-3 mb-1" style={{ color: colors.muted }}>Example</p>
            <div className="flex items-start gap-2 rounded-lg px-2 py-1.5" style={mono}>
              <span data-testid="api-example" className="flex-1 font-mono2 text-[10px] break-all whitespace-pre-wrap leading-relaxed">{`curl -X POST "${endpoint}" \\
  -H "Authorization: Bearer ${token}" \\
  -F "file=@plans.csv;type=text/csv"`}</span>
              <button data-testid="copy-example" onClick={() => copy(`curl -X POST "${endpoint}" -H "Authorization: Bearer ${token}" -F "file=@plans.csv;type=text/csv"`)} className="shrink-0 p-1 rounded-md" style={{ background: colors.surfaceSecondary }}>
                <Copy size={12} color={colors.onSurfaceTertiary} />
              </button>
            </div>
          </>
        )}

        <div className="mt-4 pt-3" style={{ borderTop: `1px solid ${colors.border}` }}>
          <button
            data-testid="dedupe-button"
            onClick={dedupe}
            disabled={deduping}
            className="flex items-center justify-center gap-2 w-full rounded-xl py-2.5 transition-transform active:scale-[0.99]"
            style={{ background: colors.surfaceTertiary, border: `1px solid ${colors.border}` }}
          >
            {deduping ? <Spinner /> : <Trash2 size={15} color={colors.onSurfaceTertiary} />}
            <span className="font-display font-bold text-[12.5px]" style={{ color: colors.onSurfaceTertiary }}>{deduping ? "Cleaning…" : "Remove duplicates"}</span>
          </button>
          <p className="font-display text-[10.5px] mt-1.5 ml-1" style={{ color: colors.faint }}>
            Optional — imports clean themselves automatically. Use this to clean instantly without waiting for the next run.
          </p>
        </div>

        <div className="mt-4 pt-3" style={{ borderTop: `1px solid ${colors.border}` }} data-testid="rename-sender-section">
          <p className="font-display font-bold text-[12.5px] mb-1" style={{ color: colors.onSurface }}>Rename a chat sender</p>
          <p className="font-display text-[10.5px] mb-2.5" style={{ color: colors.faint }}>
            Changes the "Added from chat by [name]" label on every matching plan (past &amp; future). Nothing else about the plan is touched.
          </p>
          <input
            data-testid="rename-old-name"
            value={oldName}
            onChange={(e) => setOldName(e.target.value)}
            placeholder="Current name (exact)"
            className="w-full rounded-lg px-3 py-2 mb-2 font-display text-[13px]"
            style={{ background: colors.surfaceTertiary, border: `1px solid ${colors.border}`, color: colors.onSurface }}
          />
          <input
            data-testid="rename-new-name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New name"
            className="w-full rounded-lg px-3 py-2 mb-2.5 font-display text-[13px]"
            style={{ background: colors.surfaceTertiary, border: `1px solid ${colors.border}`, color: colors.onSurface }}
          />
          <button
            data-testid="rename-sender-button"
            onClick={renameSender}
            disabled={renaming || !oldName.trim() || !newName.trim()}
            className="flex items-center justify-center gap-2 w-full rounded-xl py-2.5 transition-transform active:scale-[0.99]"
            style={{ background: colors.brand, opacity: renaming || !oldName.trim() || !newName.trim() ? 0.5 : 1 }}
          >
            {renaming ? <Spinner style={{ borderTopColor: colors.onBrand }} /> : <Check size={15} color={colors.onBrand} />}
            <span className="font-display font-bold text-[12.5px]" style={{ color: colors.onBrand }}>{renaming ? "Renaming…" : "Rename across all plans"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
